/*
 * Copyright (c) All right reserved Anvish Inc. 2025
 * This project and any portion thereof may not be reproduced or used in any manner without the express written permission of Anvish Inc.
 */
"use client";
import React, { useEffect, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { useRouter } from "next/navigation";
import Image from "next/image";
import "swiper/css";
import { Button } from "@heroui/button";

interface ServiceItemSliderProps {
  heading: string;
  servicesList: any[];
}

const ServicesCTASlider: React.FC<ServiceItemSliderProps> = ({
  heading,
  servicesList,
}) => {
  const router = useRouter();
  const sliderRef = React.useRef<any>(null);
  const [isReachedEnd, setIsReachedEnd] = useState<boolean>(false);
  const [isReachedStart, setIsReachedStart] = useState(false);

  const handlePrev = () => {
    sliderRef.current.swiper.slidePrev();
  };

  const handleNext = () => {
    sliderRef.current.swiper.slideNext();
  };

  // Extreme End Position Checker
  useEffect(() => {
    if (sliderRef.current?.swiper) {
      const swiper = sliderRef.current.swiper;

      setIsReachedStart(swiper.isBeginning);
      setIsReachedEnd(swiper.isEnd);

      // Listen for changes in swiper state
      swiper.on("slideChange", () => {
        setIsReachedStart(swiper.isBeginning);
        setIsReachedEnd(swiper.isEnd);
      });
    }
  }, [sliderRef]);

  return (
    <section className="w-full bg-[#F3F7FF]">
      <div className="flex flex-col items-start justify-start px-4 md:px-14 xl:px-[100px] max-w-[1440px] mx-auto py-10 gap-5 md:gap-10">
        <h2 className="text-2xl md:text-3xl lg:text-4xl font-medium text-center">
          {heading}
        </h2>
        <div className="w-full">
          <Swiper
            ref={sliderRef}
            breakpoints={{
              640: {
                slidesPerView: 2,
                spaceBetween: 30,
              },
              1024: {
                slidesPerView: servicesList.length > 2 ? 3 : 2,
                spaceBetween: 50,
              },
            }}
            className="w-full h-full"
            slidesPerView={1}
            spaceBetween={50}
          >
            {servicesList.map((service: any, index: number) => (
              <SwiperSlide key={index} className="!h-auto">
                <div className="flex flex-col items-center md:items-start justify-start gap-4 md:gap-10">
                  <Image
                    alt={service.value.image.title}
                    className="w-full h-auto aspect-square object-cover"
                    height={400}
                    src={service.value.image.download_url}
                    width={400}
                  />
                  <div className="flex flex-col items-center md:items-start gap-4 md:gap-6">
                    <p className="text-black font-medium text-2xl md:text-3xl">
                      {service.value.title}
                    </p>
                    <p>{service.value.description}</p>
                    <Button
                      className="bg-ax-primary font-medium"
                      radius="lg"
                      onPress={() => router.push(service.value.button_link)}
                    >
                      Shop Now
                    </Button>
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
          <div className="flex xl:hidden w-full items-center justify-center gap-3 md:gap-4 mt-6">
            <Button
              isIconOnly
              className="w-10 h-10 bg-[#F6F6F6] rounded-full"
              isDisabled={isReachedStart}
              radius="full"
              variant="flat"
              onPress={handlePrev}
            >
              <img
                alt="Left Arrow"
                className="w-5 h-5 object-contain rotate-180"
                height={20}
                src="/icons/arrow_left_black.svg"
                width={20}
              />
            </Button>
            <Button
              isIconOnly
              className="w-10 h-10 bg-[#F6F6F6] rounded-full"
              isDisabled={isReachedEnd}
              radius="full"
              variant="flat"
              onPress={handleNext}
            >
              <img
                alt="Left Arrow"
                className="w-5 h-5 object-contain"
                height={20}
                src="/icons/arrow_left_black.svg"
                width={20}
              />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesCTASlider;
